import { useCallback, useEffect, useRef, useState } from 'react';
import AnnotationEditor from './AnnotationEditor';

const CAPTURE_UPLOAD_URL = '/decoupled-api/file/upload/node/task/field_attached_files';

// Ce qui repart dans l'URL au rechargement doit tenir dans ce qu'un serveur web
// accepte comme ligne de requête (nginx : 8 Ko, au-delà c'est un 414), et le
// contexte technique en consomme déjà jusqu'à 3500 caractères. La description
// est donc bornée ICI — son texte complet est de toute façon repris en tête de
// l'historique joint, qui n'a pas cette limite.
const DESCRIPTION_MAX = 1500;
const borner = (texte) => (texte.length <= DESCRIPTION_MAX
  ? texte
  : `${texte.slice(0, DESCRIPTION_MAX)}\n\n… (texte complet dans l'historique joint)`);

/**
 * Réception de la capture d'écran envoyée par un site EXTERNE.
 *
 * Le site hôte ne peut pas téléverser lui-même : sa requête vers Orbit serait
 * cross-origin, donc sans session (cookie tiers, bloqué par Safari, cloisonné
 * par Firefox). C'est donc cette fenêtre — first-party sur Orbit — qui téléverse.
 *
 * Poignée de main : on annonce « prêt » à la fenêtre qui nous a ouverts, elle
 * répond avec le Blob (que `postMessage` transporte nativement), on propose de
 * l'ANNOTER — le même éditeur que dans la popin — puis on téléverse et on
 * recharge en ajoutant l'identifiant à l'URL. Le rechargement est sans
 * conséquence : il survient à l'ouverture, avant toute saisie, et il évite
 * d'inventer un second canal pour faire descendre l'identifiant dans le rjBuild
 * — `/report` sait déjà lire ses paramètres d'URL.
 *
 * Deux vérifications d'origine, dans les deux sens : on n'accepte un message
 * que de la fenêtre ouvrante, et le widget ne répond qu'à l'origine Orbit.
 */
/**
 * Nom UNIQUE à chaque envoi. Drupal numérote les collisions (capture.png,
 * capture_1.png…) et RÉUTILISE un nom libéré par une suppression : le navigateur
 * resservait alors sa copie en cache, donc une capture d'un signalement
 * précédent. Un nom horodaté supprime la classe entière de ce problème.
 */
const horodatage = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/** Téléverse un fichier et rend `{fid, url}`, ou null si l'envoi a échoué. */
async function televerser(blob, nom) {
  try {
    const res = await fetch(CAPTURE_UPLOAD_URL, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/octet-stream',
        'Content-Disposition': `file; filename="${nom}"`,
      },
      body: blob,
    }).then((r) => (r.ok ? r.json() : null));
    if (res?.data?.fid) return { fid: String(res.data.fid), url: res.data.url || '' };
  }
  catch { /* un fichier perdu ne doit pas empêcher de signaler */ }
  return null;
}

export default function CaptureReceiver() {
  const [blob, setBlob] = useState(null);
  // Ce que le site d'origine a envoyé avec la capture : une description
  // pré-rédigée et l'historique de conversation (assistant du builder). Gardé
  // hors état React — ces valeurs ne se rendent pas, elles accompagnent le
  // téléversement, et les toucher ne doit pas re-rendre l'éditeur d'annotation.
  const extra = useRef({ description: '', transcript: null });

  // Téléverse la capture (annotée ou non) et l'historique, puis recharge avec
  // leurs identifiants.
  const envoyer = useCallback(async (final) => {
    setBlob(null);
    const params = new URLSearchParams(window.location.search);

    // L'URL porte déjà les paramètres d'ouverture (hôte, page, contexte) : c'est
    // ce qu'on y AJOUTE qui décide s'il vaut la peine de recharger.
    let ajoute = false;

    const capture = final instanceof Blob
      ? await televerser(final, `capture-${horodatage()}.png`)
      : null;
    if (capture) {
      params.set('captureFid', capture.fid);
      params.set('captureUrl', capture.url);
      ajoute = true;
    }

    const { description, transcript } = extra.current;
    if (transcript) {
      const fichier = await televerser(transcript.blob, transcript.name || `conversation-${horodatage()}.md`);
      if (fichier) {
        params.set('transcriptFid', fichier.fid);
        ajoute = true;
      }
    }
    if (description) {
      params.set('description', borner(description));
      ajoute = true;
    }

    // Tout a échoué : recharger ferait perdre l'écran déjà en place pour rien.
    if (!ajoute) return;
    // `captureFid` est le drapeau de non-rebouclage lu au montage : il doit être
    // posé même quand la capture a échoué, sinon la poignée de main recommence.
    if (!params.has('captureFid')) params.set('captureFid', '');
    window.location.replace(`${window.location.pathname}?${params}`);
  }, []);

  useEffect(() => {
    if (!window.opener || window.opener === window) return undefined;
    if (!window.location.pathname.startsWith('/report')) return undefined;
    const params = new URLSearchParams(window.location.search);
    // Déjà passé par ici : ne pas boucler sur le rechargement.
    if (params.has('captureFid')) return undefined;

    const onMessage = async (event) => {
      if (event.source !== window.opener) return;
      const data = event.data;
      if (!data || data.type !== 'orbit-report:capture') return;
      extra.current = {
        description: typeof data.description === 'string' ? data.description : '',
        transcript: data.transcript?.blob instanceof Blob ? data.transcript : null,
      };
      // Le site peut n'avoir aucune capture (bloquée, échouée). On propose alors
      // l'annotation de rien du tout : on passe directement à l'envoi, pour que
      // la description et l'historique arrivent quand même au formulaire.
      if (!(data.blob instanceof Blob)) {
        if (extra.current.description || extra.current.transcript) envoyer(null);
        return;
      }
      setBlob(data.blob);
    };

    window.addEventListener('message', onMessage);
    // `'*'` est sans risque : ce message ne contient rien, il ne fait que dire
    // « je suis prêt ». C'est la RÉPONSE qui porte l'image, et le widget la
    // cible explicitement sur l'origine Orbit.
    window.opener.postMessage({ type: 'orbit-report:hello' }, '*');

    return () => { window.removeEventListener('message', onMessage); };
  }, [envoyer]);

  if (!blob) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-500/40 p-4 backdrop-blur-sm sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label="Annoter la capture d'écran"
    >
      <div className="max-h-[90vh] w-full max-w-4xl rounded-lg bg-white p-6 shadow-2xl">
        {/* Abandonner ne demande RIEN ici : le formulaire est déjà à l'écran
            derrière, sans capture. On referme l'éditeur, sans téléversement ni
            rechargement. */}
        <AnnotationEditor blob={blob} onDone={envoyer} onDiscard={() => setBlob(null)} />
      </div>
    </div>
  );
}
